import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import './PriceBar.css';
import '../LoadingAnimations.css';
import PriceBar from './PriceBar';

interface PriceData {
  avg: string;
  stddev: string;
}

interface PriceHistory {
  date: string;
  min_price: number;
  max_price: number;
}

const PriceData: React.FC = () => {
  const { propertyId, unitNumber } = useParams<{ propertyId: string; unitNumber: string }>();
  const [priceData, setPriceData] = useState<PriceData | null>(null);
  const [currentPrice, setCurrentPrice] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const effectivePropertyId = propertyId || localStorage.getItem('selectedPropertyId');
        const effectiveUnitNumber = unitNumber || localStorage.getItem('selectedUnitNumber');

        if (!effectivePropertyId || !effectiveUnitNumber) {
          throw new Error('Missing property or unit information');
        }

        // Fetch price range data
        const rangeResponse = await fetch(
          `https://${process.env.REACT_APP_BASE_URL}/getUnitBuildingCompRanges/${effectivePropertyId}/${effectiveUnitNumber}`
        );
        const rangeData = await rangeResponse.json();
        setPriceData(rangeData[0]);

        // Fetch current price from price history
        const historyResponse = await fetch(
          `https://${process.env.REACT_APP_BASE_URL}/getPrices/${effectivePropertyId}/${effectiveUnitNumber}/7`
        );
        const priceHistory: PriceHistory[] = await historyResponse.json();
        
        if (priceHistory.length > 0) {
          // Sort by date to get the most recent price
          const sortedPrices = priceHistory.sort((a, b) => 
            new Date(b.date).getTime() - new Date(a.date).getTime()
          );
          setCurrentPrice(sortedPrices[0].min_price);
        }
      } catch (error) {
        console.error('Error fetching price data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [propertyId, unitNumber]);

  if (isLoading) {
    return (
      <div className="price-bar-container">
        <div className="price-bar-loading" />
      </div>
    );
  }

  if (!priceData || !currentPrice) {
    return <div>Error loading price data</div>;
  }

  const avg = parseFloat(priceData.avg);
  const stddev = parseFloat(priceData.stddev);

  return (
    <PriceBar
      currentPrice={currentPrice}
      averagePrice={avg}
      standardDeviation={stddev}
    />
  );
};

export default PriceData; 